// Workspace › Tasks — one table for my work and every team I can see (11a §5). Replaces Home + My Tasks + Team Tasks.
import { useCan } from "../../hooks/useAreaLevel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import "./workspace.css";
import { workspaceApi, withMe, isMgmt, userKey, dueInfo, pinnedLink, TASK_CHANGED } from "./_shared";
import { useAuth } from "../../context/AuthContext";
import TaskTable from "./panels/TaskTable";
import TaskBoard from "./panels/TaskBoard";
import RoutineTidy from "./panels/RoutineTidy";
import TaskDrawer from "./panels/TaskDrawer";
import ListToolbar from "./panels/ListToolbar";
import TeamCard from "./panels/TeamCard";
import TeamBlocks from "./panels/TeamBlocks";
import BulkActionBar from "./panels/BulkActionBar";
import SavedViewChips, { useSavedViews } from "../../components/SavedViewChips";

const SCOPES = [["mine", "Mine"], ["claimable", "Claimable"], ["teams", "My teams"], ["all", "All visible"]];
const MGMT_SCOPES = [["everyone", "Everyone · all users"]];
const EMPTY = { q: "", status: "open", priority: "", type: "", owner: "", often: "", due: "", has: "" };
const SEEDED = [
  { name: "My overdue", snap: { scope: "mine", f: { ...EMPTY, due: "overdue" } } },
  { name: "Due this week", snap: { scope: "mine", f: { ...EMPTY, due: "week" } } },
  { name: "Recurring checks", snap: { scope: "all", f: { ...EMPTY, type: "check" } } },
  { name: "Waiting on someone", snap: { scope: "all", f: { ...EMPTY, status: "waiting" } } },
  { name: "Claimable in my teams", snap: { scope: "claimable", f: EMPTY } },
  { name: "Recently done", snap: { scope: "mine", f: { ...EMPTY, status: "recent" } } },
];

function Sel({ value, onChange, children, testid, label }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="ws-input min-w-[120px]" data-testid={testid}>{children}</select>
    </label>
  );
}

function matches(t, f) {
  if (f.status === "open" && ["done", "cancelled"].includes(t.status)) return false;
  if (f.status === "recent" && !(["done", "cancelled"].includes(t.status) && Date.now() - new Date(t.completed_at || t.updated_at) < 7 * 86_400_000)) return false;
  if (f.status && !["open", "any", "recent"].includes(f.status) && t.status !== f.status) return false;
  if (f.priority && t.priority !== f.priority) return false;
  if (f.type && t.type !== f.type) return false;
  if (f.owner === "_pool" ? !!t.assignee_id : (f.owner && t.assignee_id !== f.owner)) return false;
  const rule = t.recurrence?.rule && t.recurrence.rule !== "none" ? t.recurrence.rule : (t.recurrence_class || "none");
  if (f.often && rule !== f.often) return false;
  if (f.has === "link" && !(t.links || []).length) return false;
  if (f.has === "file" && !(t.attachments || []).length) return false;
  if (f.due) {
    const d = dueInfo(t);
    if (f.due === "overdue" && d.tone !== "red") return false;
    if (f.due === "today" && d.days !== 0) return false;
    if (f.due === "week" && !(t.due_date && d.days >= 0 && d.days <= 7 - (new Date().getDay() || 7))) return false;
    if (f.due === "none" && t.due_date) return false;
  }
  return true;
}

export default function WorkspaceTasks() {
  const can = useCan();
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(() => { const s = params.get("scope"); return s && s !== "watching" ? s : (mgmt ? "everyone" : "all"); });
  const [lens, setLens] = useState(params.get("user") || "");
  const [teamF, setTeamF] = useState(params.get("team") || "");
  const [f, setF] = useState(EMPTY);
  const qp = params.get("q") || "";
  useEffect(() => { setF((x) => (x.q === qp ? x : { ...x, q: qp })); }, [qp]);
  const [groupBy, setGroupBy] = useState("due");
  const [selected, setSelected] = useState(new Set());
  const [openTask, setOpenTask] = useState(params.get("open") || null);
  const [withArchived, setWithArchived] = useState(false);

  const userMap = useMemo(() => Object.fromEntries(withMe(users, user).map((u) => [u.id, u])), [users, user]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const snap = useMemo(() => ({ scope, f }), [scope, f]);
  const { views, saveCurrent, remove } = useSavedViews("workspace.tasks.v2.views", snap);

  const load = useCallback(async () => {
    try {
      const [c, u, tm, ts] = await Promise.all([
        workspaceApi.getConfig(), workspaceApi.listUsers(), workspaceApi.listTeams("all"),
        workspaceApi.listTasks({ scope: scope === "everyone" ? "all" : scope, ...(lens && scope !== "everyone" && { user_id: lens }),
          ...(teamF && teamF !== "_none" && { team_id: teamF }), ...(f.q && { q: f.q }), ...(withArchived && { include_archived: true }), limit: 2000 }),
      ]);
      setCfg(c); setUsers(u || []); setTeams(tm.items || []); setTasks(ts.items || []);
    } catch { toast.error("Failed to load tasks"); } finally { setLoading(false); }
  }, [scope, lens, teamF, f.q, withArchived]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { const h = () => load(); window.addEventListener(TASK_CHANGED, h); return () => window.removeEventListener(TASK_CHANGED, h); }, [load]);
  useEffect(() => setSelected(new Set()), [scope, f, teamF]);

  const [kind, setKind] = useState(() => localStorage.getItem("ws.tasks.kind") || "tasks");
  useEffect(() => { localStorage.setItem("ws.tasks.kind", kind); }, [kind]);
  const kindOk = useCallback((t) => kind === "all" || (kind === "routines") === !!t.routine, [kind]);
  const shown = useMemo(() => tasks.filter((t) => matches(t, kind === "routines" ? { ...f, due: "" } : f) && kindOk(t) && (teamF !== "_none" || !t.team_id)), [tasks, f, teamF, kind, kindOk]);
  const nRoutines = useMemo(() => tasks.filter((t) => t.routine).length, [tasks]);
  const boardTasks = useMemo(() => tasks.filter((t) => matches(t, { ...f, status: ["open", "recent"].includes(f.status) ? "any" : f.status }) && (teamF !== "_none" || !t.team_id) && !t.guide), [tasks, f, teamF]);
  const [mode, setMode] = useState(() => localStorage.getItem("ws.tasks.mode") || "table");
  useEffect(() => { localStorage.setItem("ws.tasks.mode", mode); }, [mode]);
  const statuses = cfg?.statuses || [];
  const active = Object.entries(f).filter(([k, v]) => v && !(k === "status" && v === "open") && k !== "q");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const openId = (id) => { setOpenTask(id); const p = new URLSearchParams(params); id ? p.set("open", id) : p.delete("open"); setParams(p, { replace: true }); };
  const bulk = async (patch) => { const r = await workspaceApi.batchPatch([...selected], patch); toast.success(`Updated ${r.updated.length}`); setSelected(new Set()); load(); };
  const bulkDel = async () => { const r = await workspaceApi.batchDelete([...selected]); toast.success(`Deleted ${r.deleted.length}`); setSelected(new Set()); load(); };
  const exportSel = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = shown.filter((t) => selected.has(t.id)).map((t) => [t.ref, t.title, t.type, teamMap[t.team_id]?.name || "Personal", t.status, t.priority, userMap[t.assignee_id]?.name || "", t.due_date || "", pinnedLink(t)?.url || ""].map(esc).join(","));
    const url = URL.createObjectURL(new Blob([["Ref,Title,Type,Team,Status,Priority,Owner,Due,Link", ...rows].join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "tasks.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="workspace-tasks">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">Teams</span>
        {mgmt && (
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-300 ml-2" title="Include archived tasks (e.g. older tasks moved to CRM)">
            <input type="checkbox" checked={withArchived} onChange={(e) => setWithArchived(e.target.checked)} data-testid="task-with-archived" />Include archived
          </label>
        )}
        <div className="ml-auto flex items-center gap-2">
          {mgmt && scope !== "everyone" && (
            <select value={lens} onChange={(e) => setLens(e.target.value)} className="ws-input max-w-[180px]" data-testid="task-lens">
              <option value="">Viewing: me</option>
              {users.filter((u) => u.id !== userKey(user)).map((u) => <option key={u.id} value={u.id}>Viewing: {u.name || u.email}</option>)}
            </select>
          )}
          <ListToolbar onRefresh={load} onNew={can("edit") ? () => setOpenTask("new") : null} teamId={teamF === "_none" ? "" : teamF} onImported={load} />
        </div>
      </div>
      <TeamBlocks teams={teams} value={teamF} onChange={setTeamF} totalOpen={teams.reduce((n, t) => n + (t.open_count || 0), 0)} />
      {teamMap[teamF] && (
        <TeamCard team={teamMap[teamF]} users={users} onSaved={load}
          canEdit={mgmt || (teamMap[teamF].member_ids || []).includes(userKey(user))} canEditAll={mgmt} />
      )}

      <div className="ws-card flex flex-wrap items-end gap-2" data-testid="task-filters">
        <Sel label="Status" value={f.status} onChange={set("status")} testid="task-f-status">
          <option value="open">Open (not done)</option><option value="recent">Recently done (7 days)</option><option value="any">Any</option>
          {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </Sel>
        <Sel label="Priority" value={f.priority} onChange={set("priority")} testid="task-f-priority">
          <option value="">Any</option>{["urgent", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
        </Sel>
        <Sel label="Type" value={f.type} onChange={set("type")} testid="task-f-type">
          <option value="">Any</option>{(cfg?.types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </Sel>
        <Sel label="Owner" value={f.owner} onChange={set("owner")} testid="task-f-owner">
          <option value="">Anyone</option><option value="_pool">Pool (unclaimed)</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </Sel>
        <Sel label="How often" value={f.often} onChange={set("often")} testid="task-f-often">
          <option value="">Any</option><option value="none">One-off</option><option value="every_30m">Every 30 min</option><option value="hourly">Hourly</option><option value="every_2h">Every 2 h</option><option value="daily">Daily</option><option value="weekdays">Weekdays</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="ongoing">Ongoing</option>
        </Sel>
        <Sel label="Due" value={f.due} onChange={set("due")} testid="task-f-due">
          <option value="">Any</option><option value="overdue">Overdue</option><option value="today">Today</option><option value="week">This week</option><option value="none">No date</option>
        </Sel>
        <Sel label="Has" value={f.has} onChange={set("has")} testid="task-f-has"><option value="">Any</option><option value="link">Link</option><option value="file">File</option></Sel>
        <Sel label="Group by" value={groupBy} onChange={setGroupBy} testid="task-groupby">
          {[["due", "Due"], ["team", "Team"], ["status", "Status"], ["priority", "Priority"], ["owner", "Owner"], ["type", "Type"], ["none", "None"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Sel>
        <div className="ws-seg self-end" data-testid="task-view-mode">
          {[["table", "Table"], ["board", "Board"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={mode === k ? "on" : ""} data-testid={`task-view-${k}`}>{l}</button>)}
        </div>
      </div>

      <div className="ws-card !py-2.5 flex flex-wrap items-center gap-1.5" data-testid="task-views-row">
        <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mr-1">Show</span>
        <div className="ws-seg" data-testid="task-scope">
          {[...SCOPES, ...(mgmt ? MGMT_SCOPES : [])].map(([k, l]) => <button key={k} onClick={() => setScope(k)} className={scope === k ? "on" : ""} data-testid={`task-scope-${k}`}>{l}</button>)}
        </div>
        <span className="w-px h-5 bg-white/10 mx-1.5" />
        <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mr-1">Quick views</span>
        {SEEDED.map((v) => <button key={v.name} onClick={() => { setScope(v.snap.scope); setF({ ...v.snap.f, q: qp }); }} className="ws-chip" data-testid={`task-view-${v.name.toLowerCase().replace(/\s+/g, "-")}`}>{v.name}</button>)}
        <SavedViewChips views={views} currentSnapshot={snap} onSave={saveCurrent} onApply={(s) => { setScope(!s.scope || s.scope === "watching" ? "all" : s.scope); setF({ ...EMPTY, ...(s.f || {}), q: qp }); }} onDelete={remove} testidPrefix="task-myview" />
        {active.map(([k, v]) => <button key={k} onClick={() => set(k)(k === "status" ? "open" : "")} className="ws-chip on" data-testid={`task-active-${k}`}>{k}: {v}<X className="w-3 h-3" /></button>)}
        {active.length > 0 && <button onClick={() => setF({ ...EMPTY, q: f.q })} className="text-[11px] text-slate-400 hover:text-white" data-testid="task-clear-filters">Clear all</button>}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="task-kind">
        <div className="ws-seg">
          {[["tasks", "Tasks"], ["routines", `Routines (${nRoutines})`], ["all", "All"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={kind === k ? "on" : ""} data-testid={`task-kind-${k}`}>{l}</button>)}
        </div>
        {kind === "routines" && <span className="text-[11.5px] text-slate-400">Routines repeat on their own rhythm — no due dates. Grouped by team; tick them or select several to bulk-tick.</span>}
        {kind === "routines" && mgmt && <RoutineTidy onDone={load} />}
      </div>
      {mgmt && selected.size > 0 && <BulkActionBar count={selected.size} statuses={statuses} users={users} onPatch={bulk} onDelete={bulkDel} onExport={exportSel} onClear={() => setSelected(new Set())}
        ids={[...selected]} teams={teams} onDone={() => { setSelected(new Set()); load(); }} />}

      {loading ? <div className="text-center text-slate-500 py-10">Loading…</div> : mode === "board" ? (
        <TaskBoard tasks={boardTasks} statuses={statuses} userMap={userMap} teamMap={teamMap} onOpen={(t) => openId(t.id)} onChanged={load} />
      ) : (
        <TaskTable tasks={shown} groupBy={kind === "routines" ? "team" : groupBy} statuses={statuses} userMap={userMap} teamMap={teamMap} onOpen={(t) => openId(t.id)} onChanged={load}
          selectable={mgmt} selected={selected} onToggleSelect={(t) => setSelected((p) => { const n = new Set(p); n.has(t.id) ? n.delete(t.id) : n.add(t.id); return n; })}
          onToggleAll={(on) => setSelected(on ? new Set(shown.map((t) => t.id)) : new Set())}
          emptyText={scope === "mine" ? "Nothing on your plate. Click New task to add one." : "No tasks match these filters."} />
      )}
      <div className="text-[11px] text-slate-500 font-mono" data-testid="task-count">Showing {shown.length} of {tasks.length} tasks · scope: {[...SCOPES, ...MGMT_SCOPES].find(([k]) => k === scope)?.[1]}</div>

      <TaskDrawer open={!!openTask} taskId={openTask && openTask !== "new" ? openTask : null} users={users} teams={teams}
        onClose={() => openId(null)} onSaved={() => load()} />
    </div>
  );
}
